import React, { useState, useEffect } from 'react';

const styleBlock = (
    <style>{`
    :root { --ease-out: cubic-bezier(0.16, 1, 0.3, 1); }
    @keyframes float  { 0%,100%{transform:translateY(0) scale(1)} 50%{transform:translateY(-10px) scale(1.01)} }
    @keyframes aura   { from{transform:rotate(0deg) scale(1)} to{transform:rotate(360deg) scale(1.3)} }
    @keyframes slideUp { to { opacity: 1; transform: translateY(0); } }
    @keyframes zoomIn  { to { opacity: 1; transform: scale(1); } }
    .animate-float { animation: float 6s ease-in-out infinite; }
    .animate-aura  { animation: aura 20s linear infinite alternate; }
    .slide-up { animation: slideUp 0.7s var(--ease-out) forwards; opacity: 0; transform: translateY(20px); }
    .zoom-in  { animation: zoomIn  0.6s var(--ease-out) forwards; opacity: 0; transform: scale(0.85); }
    .delay-1 { animation-delay: 0.1s; }
    .delay-2 { animation-delay: 0.2s; }
    .delay-3 { animation-delay: 0.35s; }
    .spring  { transition: all 0.5s cubic-bezier(0.34,1.56,0.64,1); }
`}</style>
);

// questions: [{id, text, basePoints, timeLimitSec, choices:[{id,text}], _correctChoiceId?}]
// onComplete: (answers:[{questionId, chosenChoiceId, timeTakenMs}]) => void
// onBack (optionnel) : retour UI depuis l'écran d'accueil (ex: revenir au leaderboard)
export default function QuizzGame({ questions, onComplete, onBack }) {
    const [step, setStep] = useState('welcome');
    const [currentQIndex, setCurrentQIndex] = useState(0);
    const [selectedChoiceId, setSelectedChoiceId] = useState(null);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [timeLeft, setTimeLeft] = useState(questions[0]?.timeLimitSec ?? 10);
    const [collectedAnswers, setCollectedAnswers] = useState([]);

    const currentQ = questions[currentQIndex];
    const hasFeedback = currentQ?._correctChoiceId != null;
    const isCritical = step === 'playing' && timeLeft <= 3 && !isTransitioning;

    useEffect(() => { setTimeLeft(currentQ?.timeLimitSec ?? 10); }, [currentQIndex]);

    useEffect(() => {
        if (step !== 'playing' || isTransitioning) return;
        if (timeLeft === 0) { recordAnswer(null, null); return; }
        const t = setInterval(() => setTimeLeft(p => p - 1), 1000);
        return () => clearInterval(t);
    }, [timeLeft, step, isTransitioning]);

    const recordAnswer = (chosenChoiceId, timeTakenMs) => {
        setIsTransitioning(true);
        const answer = { questionId: currentQ.id, chosenChoiceId, timeTakenMs };
        const updated = [...collectedAnswers, answer];
        setCollectedAnswers(updated);
        setTimeout(() => {
            setSelectedChoiceId(null);
            setIsTransitioning(false);
            if (currentQIndex < questions.length - 1) {
                setCurrentQIndex(p => p + 1);
            } else {
                onComplete?.(updated);
            }
        }, 1200);
    };

    const handleChoiceClick = (choice) => {
        if (isTransitioning) return;
        setSelectedChoiceId(choice.id);
        recordAnswer(choice.id, (currentQ.timeLimitSec - timeLeft) * 1000);
    };

    // --- FOND ---
    const Bg = () => (
        <div className="absolute inset-0 overflow-hidden pointer-events-none bg-brand-dark">
            <div className={`absolute w-[100vw] h-[100vw] md:w-[500px] md:h-[500px] rounded-full blur-[100px] animate-aura transition-colors duration-1000 ${isCritical ? 'opacity-20 bg-red-500' : 'opacity-10 bg-action-green'}`}
                style={{ top: '-20%', left: '-20%' }} />
            <div className={`absolute w-[80vw] h-[80vw] md:w-[400px] md:h-[400px] rounded-full blur-[80px] transition-colors duration-1000 ${isCritical ? 'opacity-15 bg-orange-500' : 'opacity-8 bg-yellow-400'}`}
                style={{ bottom: '-10%', right: '-10%' }} />
        </div>
    );

    // --- WELCOME ---
    if (step === 'welcome') return (
        <div className="fixed inset-0 z-50 font-sans overflow-hidden select-none flex flex-col">
            {styleBlock}
            <Bg />

            {/* Retour UI (ex: leaderboard) */}
            {onBack && (
                <button
                    onClick={onBack}
                    className="absolute top-5 left-5 z-20 w-10 h-10 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 active:scale-95 transition-all"
                    aria-label="Retour"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
            )}

            <div className="relative z-10 flex flex-col items-center justify-between h-full px-6 py-10">

                {/* Top brand */}
                <div className="slide-up text-center">
                    <p className="text-xs font-black text-gray-500 uppercase tracking-widest">
                        GOJO<span className="text-action-green">.</span> QI FOOT
                    </p>
                </div>

                {/* Centre flottant */}
                <div className="flex-1 flex flex-col items-center justify-center w-full animate-float">
                    <div className="zoom-in delay-1 w-full max-w-xs">
                        <div className="bg-white/5 border border-white/10 rounded-3xl p-8 text-center backdrop-blur-sm">
                            <div className="w-16 h-16 bg-action-green/10 border border-action-green/20 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl">
                                ⚡️
                            </div>
                            <h2 className="text-4xl font-black text-white uppercase leading-none tracking-tighter mb-3">
                                Défi<br />
                                <span className="text-yellow-400">du jour</span>
                            </h2>
                            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest flex items-center justify-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-action-green animate-pulse inline-block" />
                                {questions.length} questions · chrono
                            </p>
                        </div>
                    </div>
                </div>

                {/* CTA */}
                <div className="w-full slide-up delay-2">
                    <button
                        onClick={() => setStep('playing')}
                        className="w-full py-4 bg-action-green text-white font-black text-lg uppercase tracking-widest rounded-xl shadow-[0_10px_30px_-10px_rgba(16,185,129,0.5)] hover:scale-[1.02] active:scale-95 transition-all"
                    >
                        Lancer le défi
                    </button>
                    <p className="text-center text-[9px] text-white/20 mt-3 font-bold uppercase tracking-widest">
                        Gratuit · Sans téléchargement
                    </p>
                </div>
            </div>
        </div>
    );

    // --- PLAYING ---
    const timerPercent = (timeLeft / (currentQ.timeLimitSec ?? 10)) * 100;

    return (
        <div className="fixed inset-0 z-50 font-sans overflow-hidden select-none flex flex-col">
            {styleBlock}
            <Bg />

            <div className="relative z-10 flex flex-col h-full w-full max-w-md mx-auto px-4 pt-6 pb-4">

                {/* Watermark */}
                <div className={`absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[18rem] font-black tracking-tighter z-0 select-none pointer-events-none transition-all duration-500 ${isCritical ? 'text-red-500/10 scale-110' : 'text-white/[0.03]'}`}>
                    {timeLeft}
                </div>

                {/* HUD */}
                <div className="relative z-10 flex items-center justify-between bg-white/5 border border-white/10 backdrop-blur-sm px-4 py-2.5 rounded-full mb-6">
                    <div className="flex gap-1.5 items-center">
                        {questions.map((_, i) => (
                            <div key={i} className={`h-1.5 rounded-full transition-all duration-500 ${
                                i === currentQIndex ? 'w-7 bg-action-green shadow-[0_0_8px_rgba(16,185,129,0.6)]'
                                : i < currentQIndex ? 'w-1.5 bg-white/30'
                                : 'w-1.5 bg-white/10'
                            }`} />
                        ))}
                    </div>
                    <span className={`text-sm font-black tabular-nums tracking-widest ${isCritical ? 'text-red-400 animate-pulse' : 'text-white'}`}>
                        00:{timeLeft.toString().padStart(2, '0')}
                    </span>
                </div>

                {/* Barre timer */}
                <div className="relative z-10 h-0.5 bg-white/10 rounded-full mb-6 overflow-hidden">
                    <div
                        className={`h-full rounded-full transition-all duration-1000 linear ${isCritical ? 'bg-red-500' : 'bg-action-green'}`}
                        style={{ width: `${timerPercent}%` }}
                    />
                </div>

                {/* Question */}
                <div className="relative z-10 flex-1 flex flex-col justify-center mb-6">
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-3 text-center">
                        Question {currentQIndex + 1} / {questions.length}
                    </p>
                    <h2 className="text-2xl sm:text-3xl font-black text-white leading-[1.15] tracking-tight text-center">
                        {currentQ.text}
                    </h2>
                    <p className="text-xs font-bold text-yellow-400/60 text-center mt-3 uppercase tracking-widest">
                        {currentQ.basePoints} pts base
                    </p>
                </div>

                {/* Choix */}
                <div className="relative z-10 flex flex-col gap-2.5">
                    {currentQ.choices.map((choice) => {
                        const isSelected = selectedChoiceId === choice.id;
                        const isCorrect  = hasFeedback && choice.id === currentQ._correctChoiceId;
                        const isWrong    = hasFeedback && isTransitioning && isSelected && !isCorrect;

                        let cls = 'bg-white/5 border-white/10 text-white hover:bg-white/10 hover:border-white/20 active:scale-95';
                        let textCls = 'text-white';
                        let icon = null;

                        if (isTransitioning) {
                            if (isCorrect) {
                                cls = 'bg-action-green border-action-green scale-[1.04] shadow-[0_0_32px_rgba(16,185,129,0.55)] z-10';
                                textCls = 'text-white font-black';
                                icon = <span className="text-white font-black text-2xl animate-bounce">✓</span>;
                            } else if (isWrong) {
                                cls = 'bg-red-500 border-red-500 scale-[0.97] shadow-[0_0_20px_rgba(239,68,68,0.4)]';
                                textCls = 'text-white font-black';
                                icon = <span className="text-white font-black text-2xl">✕</span>;
                            } else if (hasFeedback) {
                                cls = 'bg-white/[0.02] border-white/5 opacity-25 scale-[0.97] blur-[1px]';
                                textCls = 'text-gray-500';
                            } else if (isSelected) {
                                cls = 'bg-white/20 border-white/40 scale-[1.02] shadow-[0_0_16px_rgba(255,255,255,0.1)]';
                                textCls = 'text-white font-black';
                            } else {
                                cls = 'bg-white/[0.02] border-white/5 opacity-25 scale-[0.97]';
                                textCls = 'text-gray-500';
                            }
                        }

                        return (
                            <button
                                key={choice.id}
                                onClick={() => handleChoiceClick(choice)}
                                disabled={isTransitioning}
                                className={`w-full text-left py-4 px-5 rounded-2xl border spring flex items-center justify-between ${cls}`}
                            >
                                <span className={`text-base sm:text-lg tracking-tight ${textCls}`}>
                                    {choice.text}
                                </span>
                                {icon}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
